import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "../../lib/api";
import { Header } from "../../components/vc/Header";
import { EmptyState, Section, SkeletonList } from "../../components/vc/layout";
import { SecondaryButton } from "../../components/vc/Buttons";
import { ArrowLeft, Trash2, Mail, Briefcase, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";

/** Phase 6 #5-8 — Deleted Employees: soft-deleted staff only, with a
 * Reactivate action that restores the exact same record (same id/employee_code). */
export default function VCDeletedEmployees() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null); // employee to reactivate
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    apiClient.get("/employees/deleted").then((r) => setEmployees(r.data || [])).catch(() => setEmployees([])).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const reactivate = async () => {
    if (!target) return;
    setBusy(true);
    try {
      await apiClient.post(`/employees/${target.id}/reactivate`);
      toast.success(`${target.name} reactivated`);
      setTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to reactivate");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Header />
      <div className="px-4 pt-4 pb-28 space-y-4">
        <button
          onClick={() => navigate("/employees")}
          data-testid="deleted-employees-back"
          className="flex items-center gap-1 text-sm font-semibold text-slate-600"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div>
          <h2 className="text-2xl font-bold text-slate-900 leading-tight">Deleted Employees</h2>
          <p className="text-sm text-slate-500 mt-0.5">{employees.length} deactivated</p>
        </div>

        <Section title="Deactivated" subtitle={`${employees.length}`} testid="deleted-employees-section">
          {loading ? <SkeletonList height="h-24" /> : employees.length === 0 ? (
            <EmptyState Icon={Trash2} title="No deleted employees" subtitle="Anyone you delete will show up here for reactivation" />
          ) : (
            <div className="space-y-2" data-testid="deleted-employees-list">
              {employees.map((e) => (
                <DeletedEmployeeCard key={e.id} employee={e} onReactivate={() => setTarget(e)} />
              ))}
            </div>
          )}
        </Section>
      </div>

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent className="max-w-sm rounded-3xl" data-testid="reactivate-dialog">
          <DialogHeader>
            <DialogTitle>Reactivate Employee?</DialogTitle>
            <DialogDescription>Do you want to reactivate this employee?</DialogDescription>
          </DialogHeader>
          <p className="text-xs text-slate-500">
            {target?.name} will return to your active team with the same Employee ID, login email and password.
          </p>
          <div className="grid grid-cols-2 gap-2 pt-1">
            <SecondaryButton onClick={() => setTarget(null)} data-testid="reactivate-cancel">Cancel</SecondaryButton>
            <button
              onClick={reactivate}
              disabled={busy}
              data-testid="reactivate-confirm"
              className="h-11 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50"
            >
              {busy ? "Reactivating..." : "Yes, Reactivate"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function DeletedEmployeeCard({ employee, onReactivate }) {
  const initial = (employee.name || "?").charAt(0).toUpperCase();
  const deletedDate = employee.deleted_at
    ? new Date(employee.deleted_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : null;
  return (
    <div data-testid={`deleted-employee-${employee.id}`} className="bg-white border border-slate-100 rounded-2xl p-4 opacity-90">
      <div className="flex items-start gap-3">
        {employee.profile_photo ? (
          <img src={employee.profile_photo} alt={employee.name} className="w-12 h-12 rounded-2xl object-cover shrink-0 grayscale" />
        ) : (
          <div className="w-12 h-12 rounded-2xl bg-slate-100 text-slate-500 grid place-items-center shrink-0 font-bold">{initial}</div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="font-semibold text-slate-700 truncate">{employee.name}</div>
            <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-500" data-testid={`deleted-employee-status-${employee.id}`}>
              Deleted
            </span>
          </div>
          <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1"><Briefcase className="w-3 h-3" /> {employee.designation || employee.role || "Staff"}</div>
          {employee.email && (
            <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-1 break-all"><Mail className="w-3 h-3" /> {employee.email}</div>
          )}
          <div className="text-[10px] text-slate-400 mt-1 font-mono">ID · {employee.employee_code}</div>
          {deletedDate && <div className="text-[10px] text-slate-400 mt-0.5">Deleted {deletedDate}</div>}
        </div>
      </div>
      <button
        onClick={onReactivate}
        data-testid={`reactivate-btn-${employee.id}`}
        className="mt-3 w-full h-10 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-bold flex items-center justify-center gap-1.5 transition"
      >
        <RotateCcw className="w-3.5 h-3.5" /> Reactivate Employee
      </button>
    </div>
  );
}
